import { useCallback } from 'react';
import { useAuth } from '~/app/auth/context/AuthContext';
import { uploadAssetImport } from '~/app/services/asset/asset-import.service';
import { createCsvFileFromRows } from '~/app/utils/convertToCsv';
import type {
  AssetImportFeedbackControls,
  AssetImportMappingState,
} from '../assetImportMatchy.types';
import {
  buildAvailableFields,
  buildMappedFileName,
  buildNormalizedHeaders,
} from '../assetImportMatchy.utils';

type UseAssetImportUploadParams = AssetImportFeedbackControls &
  AssetImportMappingState & {
    selectedAssetModelId: string;
    selectedAssetTypeId: string;
  };

export function useAssetImportUpload(params: UseAssetImportUploadParams) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const {
    currentSuggestions,
    fileName,
    rawHeaders,
    selectedAssetModelId,
    selectedAssetTypeId,
    setConfirmationMessageAction,
    setCsvRowsAction,
    setErrorAction,
    setStatusAction,
    stagedUploadRows,
  } = params;

  const handleUploadData = useCallback(() => {
    const uploadConfirmedRows = async () => {
      if (!selectedAssetTypeId) {
        setStatusAction('error');
        setErrorAction('Select an asset type before uploading.');
        return;
      }

      if (!selectedAssetModelId) {
        setStatusAction('error');
        setErrorAction('Select an asset model before uploading.');
        return;
      }

      if (stagedUploadRows.length === 0) {
        setStatusAction('error');
        setErrorAction('No mapped rows are available to upload.');
        return;
      }

      setConfirmationMessageAction(null);
      setErrorAction(null);
      setStatusAction('parsing');

      try {
        const mappedFile = createCsvFileFromRows(
          stagedUploadRows,
          buildMappedFileName(fileName),
        );
        const normalizedHeaders = buildNormalizedHeaders({
          headers: rawHeaders,
          suggestions: currentSuggestions,
          fallbackRows: stagedUploadRows,
        });

        const response = await uploadAssetImport({
          organizationId,
          file: mappedFile,
          assetTypeId: selectedAssetTypeId,
          assetModelId: selectedAssetModelId,
          suggestions: currentSuggestions,
          headers: normalizedHeaders,
          availableFields: buildAvailableFields(),
        });

        setCsvRowsAction(stagedUploadRows);
        setStatusAction(response.status === 'FAILED' ? 'error' : 'success');

        if (response.status === 'FAILED') {
          const firstError = response.report.errors[0]?.message;
          setErrorAction(firstError ?? 'Asset import failed.');
          return;
        }

        setConfirmationMessageAction(
          `Upload completed. ${response.totalAssets} asset${response.totalAssets === 1 ? '' : 's'} processed.`,
        );
      } catch (nextError) {
        setStatusAction('error');
        setErrorAction(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to upload mapped asset import file',
        );
      }
    };

    void uploadConfirmedRows();
  }, [
    currentSuggestions,
    fileName,
    rawHeaders,
    selectedAssetModelId,
    selectedAssetTypeId,
    setConfirmationMessageAction,
    setCsvRowsAction,
    setErrorAction,
    setStatusAction,
    stagedUploadRows,
  ]);

  return {
    handleUploadData,
  };
}
